import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ChevronLeft, Loader2, CheckCircle, Clock, AlertCircle, XCircle,
  Home, User, PoundSterling, Building2, FileText, Briefcase,
  ArrowRight, Calendar, Phone, Mail, MapPin, Scale
} from "lucide-react";
import { API } from "@/App";

const STATUS_CONFIG = {
  active: { label: "SSTC", color: "bg-blue-100 text-blue-700", icon: Clock },
  exchanged: { label: "Exchanged", color: "bg-purple-100 text-purple-700", icon: CheckCircle },
  completed: { label: "Completed", color: "bg-green-100 text-green-700", icon: CheckCircle },
  fallen_through: { label: "Fallen Through", color: "bg-red-100 text-red-700", icon: XCircle }
};

const MILESTONE_CONFIG = {
  sale_agreed: { label: "Sale Agreed", icon: CheckCircle },
  memorandum_issued: { label: "Memorandum Issued", icon: FileText },
  solicitors_instructed: { label: "Solicitors Instructed", icon: Scale },
  mortgage_submitted: { label: "Mortgage Submitted", icon: Building2 },
  mortgage_offer: { label: "Mortgage Offer Received", icon: CheckCircle },
  survey_booked: { label: "Survey Booked", icon: Calendar },
  survey_received: { label: "Survey Received", icon: FileText },
  searches_contract_initiated: { label: "Searches/Contract Initiated", icon: FileText },
  searches_returned: { label: "Searches Returned", icon: CheckCircle },
  enquiries_raised: { label: "Enquiries Raised", icon: FileText },
  enquiries_satisfied: { label: "Enquiries Satisfied", icon: CheckCircle },
  exchange: { label: "Exchange", icon: ArrowRight },
  completion: { label: "Completion", icon: Home }
};

const MILESTONE_STATUS_CONFIG = {
  not_started: { label: "Not Started", color: "bg-slate-100 text-slate-600" },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700" },
  completed: { label: "Completed", color: "bg-green-100 text-green-700" },
  blocked: { label: "Blocked", color: "bg-red-100 text-red-700" }
};

export const SalesProgressionPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [caseData, setCaseData] = useState(null);
  const [parties, setParties] = useState([]);
  const [listing, setListing] = useState(null);
  const [offer, setOffer] = useState(null);
  const [progress, setProgress] = useState(0);
  const [activeTab, setActiveTab] = useState("milestones");
  
  // Modals
  const [showSolicitorModal, setShowSolicitorModal] = useState(false);
  const [solicitorParty, setSolicitorParty] = useState("buyer");
  const [solicitorForm, setSolicitorForm] = useState({
    solicitor_name: "",
    solicitor_firm: "",
    solicitor_email: "",
    solicitor_phone: ""
  });
  
  const [showFallThroughModal, setShowFallThroughModal] = useState(false);
  const [fallThroughReason, setFallThroughReason] = useState("");

  const fetchCase = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/progression/sales/${caseId}`, { headers });
      setCaseData(res.data.case);
      setParties(res.data.parties || []);
      setListing(res.data.listing);
      setOffer(res.data.offer);
      setProgress(res.data.progress || 0);
    } catch (error) {
      console.error("Error fetching case:", error);
      toast.error("Failed to load case");
    } finally {
      setLoading(false);
    }
  }, [token, caseId]);

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  const handleMilestoneUpdate = async (milestoneKey, newStatus) => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/progression/sales/milestone`, {
        case_id: caseId,
        milestone_key: milestoneKey,
        status: newStatus,
        date: newStatus === "completed" ? new Date().toISOString().split("T")[0] : null
      }, { headers });
      
      toast.success("Milestone updated");
      fetchCase();
    } catch (error) {
      toast.error("Failed to update milestone");
    }
  };

  const handleAssignSolicitor = async () => {
    if (!solicitorForm.solicitor_name || !solicitorForm.solicitor_firm) {
      toast.error("Please enter solicitor name and firm");
      return;
    }
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/progression/sales/solicitor`, {
        case_id: caseId,
        party: solicitorParty,
        ...solicitorForm
      }, { headers });
      
      toast.success(`${solicitorParty.charAt(0).toUpperCase() + solicitorParty.slice(1)} solicitor assigned`);
      setShowSolicitorModal(false);
      setSolicitorForm({ solicitor_name: "", solicitor_firm: "", solicitor_email: "", solicitor_phone: "" });
      fetchCase();
    } catch (error) {
      toast.error("Failed to assign solicitor");
    }
  };

  const handleFallThrough = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/progression/sales/${caseId}/fall-through?reason=${encodeURIComponent(fallThroughReason)}`, {}, { headers });
      
      toast.success("Case marked as fallen through");
      setShowFallThroughModal(false);
      fetchCase();
    } catch (error) {
      toast.error("Failed to update case");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-600" />
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-slate-500">Case not found</p>
      </div>
    );
  }

  const statusConfig = STATUS_CONFIG[caseData.status] || STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;
  const milestones = caseData.milestones || {};

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <Button variant="ghost" onClick={() => navigate(-1)} className="mb-4 text-white/80 hover:text-white hover:bg-white/10">
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          <div className="flex items-start justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className={`${statusConfig.color} px-3 py-1`}>
                  <StatusIcon className="w-4 h-4 mr-1" />
                  {statusConfig.label}
                </Badge>
                <Badge variant="outline" className="text-white border-white/40">
                  Sales Progression
                </Badge>
              </div>
              <h1 className="text-2xl font-bold">{caseData.address_line1}</h1>
              <p className="text-white/70 mt-1">{caseData.postcode}</p>
            </div>
            <div className="text-right">
              <p className="text-white/70 text-sm">Sale Price</p>
              <p className="text-3xl font-bold">
                £{(caseData.offer_price || 0).toLocaleString()}
              </p>
              <div className="mt-2">
                <p className="text-white/70 text-sm">Progress</p>
                <div className="flex items-center gap-2">
                  <Progress value={progress} className="w-32 h-2" />
                  <span className="font-medium">{progress}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="milestones">Milestones</TabsTrigger>
            <TabsTrigger value="parties">Parties</TabsTrigger>
            <TabsTrigger value="details">Details</TabsTrigger>
          </TabsList>

          {/* Milestones Tab */}
          <TabsContent value="milestones">
            <Card>
              <CardHeader>
                <CardTitle>Sales Progression Milestones</CardTitle>
                <CardDescription>Track the progress of this sale</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {Object.entries(MILESTONE_CONFIG).map(([key, config], index) => {
                    const milestone = milestones[key] || { status: "not_started" };
                    const milestoneStatus = MILESTONE_STATUS_CONFIG[milestone.status] || MILESTONE_STATUS_CONFIG.not_started;
                    const Icon = config.icon;
                    
                    return (
                      <div 
                        key={key}
                        className={`flex items-center gap-4 p-4 rounded-lg border transition-colors ${
                          milestone.status === "completed" ? "bg-green-50 border-green-200" :
                          milestone.status === "in_progress" ? "bg-amber-50 border-amber-200" :
                          milestone.status === "blocked" ? "bg-red-50 border-red-200" :
                          "bg-slate-50 border-slate-200"
                        }`}
                      >
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white border">
                          <span className="text-sm font-medium text-slate-600">{index + 1}</span>
                        </div>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Icon className={`w-4 h-4 ${
                              milestone.status === "completed" ? "text-green-600" :
                              milestone.status === "in_progress" ? "text-amber-600" :
                              "text-slate-400"
                            }`} />
                            <span className={`font-medium ${
                              milestone.status === "completed" ? "text-green-700" : "text-slate-700"
                            }`}>
                              {config.label}
                            </span>
                          </div>
                          {milestone.date && (
                            <p className="text-xs text-slate-500 mt-1">
                              {milestone.status === "completed" ? "Completed" : "Updated"}: {milestone.date}
                            </p>
                          )}
                        </div>
                        
                        <Select 
                          value={milestone.status} 
                          onValueChange={(v) => handleMilestoneUpdate(key, v)}
                          disabled={caseData.status === "fallen_through" || caseData.status === "completed"}
                        >
                          <SelectTrigger className="w-36">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="not_started">Not Started</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="blocked">Blocked</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    );
                  })}
                </div>
                
                {caseData.status === "active" && (
                  <div className="mt-6 pt-6 border-t">
                    <Button 
                      variant="destructive" 
                      onClick={() => setShowFallThroughModal(true)}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Mark as Fallen Through
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parties Tab */}
          <TabsContent value="parties">
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Seller */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Seller
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-medium">{caseData.seller_name || "Not specified"}</p>
                    {caseData.seller_email && (
                      <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                        <Mail className="w-4 h-4" />
                        {caseData.seller_email}
                      </p>
                    )}
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium flex items-center gap-2">
                        <Scale className="w-4 h-4" />
                        Seller's Solicitor
                      </h4>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSolicitorParty("seller");
                          setSolicitorForm({
                            solicitor_name: caseData.seller_solicitor?.name || "",
                            solicitor_firm: caseData.seller_solicitor?.firm || "",
                            solicitor_email: caseData.seller_solicitor?.email || "",
                            solicitor_phone: caseData.seller_solicitor?.phone || ""
                          });
                          setShowSolicitorModal(true);
                        }}
                      >
                        {caseData.seller_solicitor?.name ? "Edit" : "Assign"}
                      </Button>
                    </div>
                    {caseData.seller_solicitor?.name ? (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="font-medium">{caseData.seller_solicitor.name}</p>
                        <p className="text-sm text-slate-600">{caseData.seller_solicitor.firm}</p>
                        {caseData.seller_solicitor.email && (
                          <p className="text-sm text-slate-500">{caseData.seller_solicitor.email}</p>
                        )}
                        {caseData.seller_solicitor.phone && (
                          <p className="text-sm text-slate-500">{caseData.seller_solicitor.phone}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No solicitor assigned</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Buyer */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Buyer
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <p className="font-medium">{caseData.buyer_name || "Not specified"}</p>
                    {caseData.buyer_email && (
                      <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                        <Mail className="w-4 h-4" />
                        {caseData.buyer_email}
                      </p>
                    )}
                    {caseData.buyer_position && (
                      <Badge className="mt-2" variant="outline">
                        {caseData.buyer_position.replace(/_/g, " ").toUpperCase()}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="border-t pt-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium flex items-center gap-2">
                        <Scale className="w-4 h-4" />
                        Buyer's Solicitor
                      </h4>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => {
                          setSolicitorParty("buyer");
                          setSolicitorForm({
                            solicitor_name: caseData.buyer_solicitor?.name || "",
                            solicitor_firm: caseData.buyer_solicitor?.firm || "",
                            solicitor_email: caseData.buyer_solicitor?.email || "",
                            solicitor_phone: caseData.buyer_solicitor?.phone || ""
                          });
                          setShowSolicitorModal(true);
                        }}
                      >
                        {caseData.buyer_solicitor?.name ? "Edit" : "Assign"}
                      </Button>
                    </div>
                    {caseData.buyer_solicitor?.name ? (
                      <div className="p-3 bg-blue-50 rounded-lg">
                        <p className="font-medium">{caseData.buyer_solicitor.name}</p>
                        <p className="text-sm text-slate-600">{caseData.buyer_solicitor.firm}</p>
                        {caseData.buyer_solicitor.email && (
                          <p className="text-sm text-slate-500">{caseData.buyer_solicitor.email}</p>
                        )}
                        {caseData.buyer_solicitor.phone && (
                          <p className="text-sm text-slate-500">{caseData.buyer_solicitor.phone}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No solicitor assigned</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Details Tab */}
          <TabsContent value="details">
            <div className="grid lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Sale Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Sale Price</p>
                      <p className="font-bold text-lg">£{(caseData.offer_price || 0).toLocaleString()}</p>
                    </div>
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-500">Status</p>
                      <Badge className={statusConfig.color}>{statusConfig.label}</Badge>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Property</p>
                    <p className="font-medium">{caseData.address_line1}</p>
                    <p className="text-sm text-slate-600">{caseData.postcode}</p>
                  </div>
                  
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-500">Case Created</p>
                    <p className="font-medium">{new Date(caseData.created_at).toLocaleDateString()}</p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Linked Records</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {listing && (
                    <div 
                      className="p-4 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/listings/${listing.listing_id}`)}
                    >
                      <div className="flex items-center gap-2">
                        <Home className="w-5 h-5 text-blue-600" />
                        <span className="font-medium">Listing</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-1">{listing.property?.full_address}</p>
                      <Badge className="mt-2" variant="outline">{listing.status}</Badge>
                    </div>
                  )}
                  
                  {offer && (
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2">
                        <PoundSterling className="w-5 h-5 text-green-600" />
                        <span className="font-medium">Accepted Offer</span>
                      </div>
                      <p className="text-lg font-bold mt-1">£{(offer.offer_amount || 0).toLocaleString()}</p>
                      <p className="text-sm text-slate-500">from {offer.contact_name}</p>
                    </div>
                  )}
                  
                  {caseData.contact_id && (
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate(`/contacts/${caseData.seller_id}`)}
                    >
                      <User className="w-4 h-4 mr-2" />
                      View Seller Contact
                    </Button>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Solicitor Modal */}
      <Dialog open={showSolicitorModal} onOpenChange={setShowSolicitorModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign {solicitorParty.charAt(0).toUpperCase() + solicitorParty.slice(1)}'s Solicitor</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Solicitor Name *</Label>
              <Input
                value={solicitorForm.solicitor_name}
                onChange={(e) => setSolicitorForm({...solicitorForm, solicitor_name: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Firm Name *</Label>
              <Input
                value={solicitorForm.solicitor_firm}
                onChange={(e) => setSolicitorForm({...solicitorForm, solicitor_firm: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={solicitorForm.solicitor_email}
                onChange={(e) => setSolicitorForm({...solicitorForm, solicitor_email: e.target.value})}
                className="mt-1"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={solicitorForm.solicitor_phone}
                onChange={(e) => setSolicitorForm({...solicitorForm, solicitor_phone: e.target.value})}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSolicitorModal(false)}>Cancel</Button>
            <Button onClick={handleAssignSolicitor}>Assign Solicitor</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fall Through Modal */}
      <Dialog open={showFallThroughModal} onOpenChange={setShowFallThroughModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark as Fallen Through</DialogTitle>
            <DialogDescription>
              This will mark the sale as fallen through and re-list the property.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason</Label>
            <Textarea
              value={fallThroughReason}
              onChange={(e) => setFallThroughReason(e.target.value)}
              className="mt-1"
              rows={3}
              placeholder="Why did this sale fall through?"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFallThroughModal(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleFallThrough}>Confirm Fall Through</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SalesProgressionPage;
